import React, { ReactNode, useEffect, useState } from "react";
import { Button, Card, Dialog, DialogActions, DialogContent, DialogTitle, FlexGrid, Typography, useDialog } from "@albo-ui/core";
import { Box, Chip, Alert, AlertTitle } from "@mui/material";
import { ErrorOutline, InfoOutlined, CheckCircleOutline, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { ModalBlackListDialog, typeBlackList } from "./ModalBlacklist";
import { ModalManualCheck } from "./ModalManualCheck";
import {
    APPROVED_BY_MANUAL_MESSAGE,
    APPROVED_BY_MANUAL_STATUS_LABEL,
    BlacklistStatusType,
    ManualCheckPayload,
    ManualCheckSummary,
    MatchInfo,
    PLD_COLORS,
    PLD_STATUS_DICTIONARY,
    StatusStyleConfig,
    formatManualCheckMeta,
    getEntitiesText,
    getErrorAlertMessage,
    getExternalMatchInfo,
    getFailedSources,
    getManualCheckSummary,
    getMatchInfo,
    getSourcesText,
    resolvePldStatus,
} from "./pldStatus";

// Re-export para mantener la API pública existente
export { PLD_STATUS_DICTIONARY, resolvePldStatus };
export type { BlacklistStatusType, StatusStyleConfig };

export type BlacklistCruiseSectionProps = {
    open?: boolean;
    icon?: ReactNode;
    title: string;
    onClose?: () => void;
    changeAlert?: (show: boolean, type: string, message: string) => void;
    lead?: any;
    initialStatus?: BlacklistStatusType;
    onRetryCruise?: () => void;
    onGenerateCruise?: () => void;
    onSearch?: (sectionName: string) => void;
    onSetClean?: (clean?: boolean) => void;
    externalValidations?: Array<any>;
    internalValidations?: Array<any>;
    onSaveManualCheck?: (payload: ManualCheckPayload) => Promise<boolean>;
    canRetryCruise?: boolean;
    canManualCheck?: boolean;
    canGenerateCruise?: boolean;
};

// Estatus para los que aplica la fila "Cruce manual (manual check)"
const MANUAL_ROW_STATUSES: BlacklistStatusType[] = ["not_executed", "manual_review", "approved", "match_confirmed", "manual_rejected"];

const DetailLink = ({ count, onClick }: { count: number; onClick: () => void }) => (
    <Typography
        variant="paragraph"
        style={{ color: PLD_COLORS.blue, textDecoration: "underline", cursor: "pointer", marginLeft: 12, fontSize: "0.85rem", fontWeight: 600 }}
        onClick={onClick}
    >
        Ver detalle ({count})
    </Typography>
);

const InfoRow = ({ label, children }: { label: string; children: ReactNode }) => (
    <>
        <Typography variant="paragraph" color="lightNeutral">
            {label}
        </Typography>
        {children}
    </>
);

const MatchInfoRow = ({ label, info, count, onOpen, emptyMessage }: { label: string; info: MatchInfo; count: number; onOpen: () => void; emptyMessage: string }) => (
    <InfoRow label={label}>
        {count === 0 ? (
            <Typography variant="paragraph" style={{ color: PLD_COLORS.neutral, fontWeight: 600 }}>
                {emptyMessage}
            </Typography>
        ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="paragraph" style={{ color: info.color, fontWeight: 600 }}>
                    {info.text}
                </Typography>
                <DetailLink count={count} onClick={onOpen} />
            </Box>
        )}
    </InfoRow>
);

const ManualCheckValue = ({ summary, pldStatus, manualApplicable }: { summary: ManualCheckSummary; pldStatus: BlacklistStatusType; manualApplicable: boolean }) => {
    if (summary.hasRejected) {
        return (
            <Typography variant="paragraph" style={{ color: PLD_COLORS.red, fontWeight: 600 }}>
                Cruce no aprobado{formatManualCheckMeta(summary.manualCheck)}
            </Typography>
        );
    }
    if (summary.hasCleared) {
        return (
            <Typography variant="paragraph" style={{ color: PLD_COLORS.green, fontWeight: 600 }}>
                Cruce aprobado{formatManualCheckMeta(summary.manualCheck)}
            </Typography>
        );
    }
    if (pldStatus === "manual_review") {
        return (
            <Typography variant="paragraph" style={{ color: PLD_COLORS.orange, fontWeight: 600 }}>
                Pendiente — abre los cruces realizados para registrar el resultado
            </Typography>
        );
    }

    // El cruce manual solo aplica a listas restringidas (external)
    if (!manualApplicable) {
        return (
            <Typography variant="paragraph" style={{ color: PLD_COLORS.neutral, fontWeight: 600 }}>
                No aplica
            </Typography>
        );
    }
    return (
        <Typography variant="paragraph" style={{ color: PLD_COLORS.red, fontWeight: 600 }}>
            Coincidencia exacta
        </Typography>
    );
};

export const BlacklistCruiseSection = ({
    open = true,
    icon,
    title,
    onClose,
    changeAlert,
    initialStatus = "manual_review",
    onRetryCruise,
    onGenerateCruise,
    onSearch,
    onSetClean,
    externalValidations = [],
    internalValidations = [],
    onSaveManualCheck,
    canRetryCruise = true,
    canManualCheck = true,
    canGenerateCruise = true,
}: BlacklistCruiseSectionProps) => {
    const [pldStatus, setPldStatus] = useState<BlacklistStatusType>(initialStatus);
    const [activeModalData, setActiveModalData] = useState<{ title: string; list: Array<any> } | null>(null);
    const [matchDetails, setMatchDetails] = useState<any>(null);

    const modalDetailProps = useDialog();
    const modalManualCheckProps = useDialog();

    // El estatus se recalcula cuando llegan validaciones de las APIs.
    // Si no llega ninguna (ambas fuentes vacías), el cruce queda como "no ejecutado".
    useEffect(() => {
        setPldStatus(resolvePldStatus(externalValidations, internalValidations, "not_executed") as BlacklistStatusType);
    }, [externalValidations, internalValidations]);

    const handleRetry = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        if (onRetryCruise) {
            onRetryCruise();
            return;
        }
        changeAlert?.(true, "info", "Reintentando cruce en listas restringidas y blacklist interna...");
        onSearch?.("COMPANY_INFO");
    };

    const handleGenerateCruise = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        onGenerateCruise?.();
    };

    const onShowInfo = () => {
        if (!open) {
            onSearch?.("COMPANY_INFO");
        } else {
            onSetClean?.(false);
        }
    };

    const openDetailModal = (list: Array<any>, title: string) => {
        setActiveModalData({ title, list });
        modalDetailProps.openDialog();
    };

    const openExternalModal = () => openDetailModal(externalValidations, "Cruces realizados - Listas restringidas");
    const openInternalModal = () => openDetailModal(internalValidations, "Cruces realizados - Blacklist interna");

    const isExternalModalOpen = activeModalData?.list === externalValidations;
    const allValidations = [...externalValidations, ...internalValidations];
    const hasAnyValidations = allValidations.length > 0;

    const currentConfig = PLD_STATUS_DICTIONARY[pldStatus] || PLD_STATUS_DICTIONARY.manual_review;
    const manualSummary = getManualCheckSummary(allValidations);
    const approvedByManual = pldStatus === "approved" && manualSummary.hasCleared;

    // Si hay coincidencia en blacklist interna, el caso queda como coincidencia exacta:
    // no se permite cruce manual, por lo que el detalle externo no muestra la columna Acción.
    const internalHasMatch = internalValidations.some((i) => i?.result === "matched");
    const externalHasMatch = externalValidations.some((i) => i?.result === "matched");
    const showManualCheckColumn = isExternalModalOpen && !internalHasMatch && canManualCheck;

    const statusLabel = approvedByManual ? APPROVED_BY_MANUAL_STATUS_LABEL : currentConfig.statusLabel;
    const externalMatchInfo = getExternalMatchInfo(externalValidations);
    const internalMatchInfo = getMatchInfo(internalValidations);
    const entitiesText = getEntitiesText(allValidations, typeBlackList);
    const sourcesText = getSourcesText(externalValidations, internalValidations);

    const failedSources = getFailedSources(externalValidations, internalValidations);
    const errorAlertMessage = getErrorAlertMessage(pldStatus === "incomplete_error" ? failedSources : [], currentConfig);
    const alertMessage = approvedByManual ? APPROVED_BY_MANUAL_MESSAGE : errorAlertMessage;
    const showManualRow = MANUAL_ROW_STATUSES.includes(pldStatus);

    const renderContent = () => (
        <FlexGrid container direction="column" spacing={2} style={{ width: "100%", marginBottom: 20 }}>
            <Card
                padding={open ? 3 : 2}
                fullWidth
                style={{
                    border: currentConfig.border,
                    height: "auto",
                    flexDirection: "column",
                    justifyContent: !open ? "center" : "flex-start",
                    boxSizing: "border-box",
                }}
            >
                <FlexGrid container direction="column" spacing={open ? 3 : 0} fullWidth>
                    {/* Header del Card con botón de colapso/despliegue */}
                    <FlexGrid container justify="space-between" alignItems="center" style={{ width: "100%", cursor: "pointer" }} onClick={onShowInfo}>
                        <FlexGrid item style={{ alignItems: "center" }}>
                            <Typography variant="h2" style={{ display: "inline-flex", alignItems: "center", fontSize: "1.2rem", width: "100%" }}>
                                {icon}
                                {title}
                                <Chip
                                    label={currentConfig.label}
                                    size="small"
                                    sx={{ backgroundColor: currentConfig.badgeBg, color: currentConfig.badgeColor, fontWeight: 700, ml: 2 }}
                                />
                            </Typography>
                        </FlexGrid>

                        <FlexGrid item>
                            {open && pldStatus === "incomplete_error" && hasAnyValidations && canRetryCruise && (
                                <Button size="normal" onClick={handleRetry} style={{ backgroundColor: "#2B368B", color: "#FFFFFF" }}>
                                    Reintentar cruce manual
                                </Button>
                            )}
                            {open && !hasAnyValidations && canGenerateCruise && (
                                <Button size="normal" onClick={handleGenerateCruise} style={{ backgroundColor: "#2B368B", color: "#FFFFFF" }}>
                                    Generar cruce
                                </Button>
                            )}
                            {open ? (
                                <KeyboardArrowUp sx={{ cursor: "pointer", color: PLD_COLORS.neutral, fontSize: 28, ml: 2 }} />
                            ) : (
                                <KeyboardArrowDown sx={{ cursor: "pointer", color: PLD_COLORS.neutral, fontSize: 28 }} />
                            )}
                        </FlexGrid>
                    </FlexGrid>

                    {/* Contenido desplegable */}
                    {open && (
                        <>
                            <Box sx={{ display: "grid", gridTemplateColumns: "240px 1fr", rowGap: 1.5, columnGap: 2, fontSize: "0.9rem", my: 1 }}>
                                <MatchInfoRow label="Coincidencias en listas restringidas" info={externalMatchInfo} count={externalValidations.length} onOpen={openExternalModal} emptyMessage="Las validaciones de listas restringidas no se han ejecutado" />
                                <MatchInfoRow label="Coincidencias en blacklist interna" info={internalMatchInfo} count={internalValidations.length} onOpen={openInternalModal} emptyMessage="Las validaciones de blacklist interna no se han ejecutado" />

                                <InfoRow label="Estado del cruce">
                                    <Typography variant="paragraph" style={{ fontWeight: 500 }}>
                                        {statusLabel}
                                    </Typography>
                                </InfoRow>
                                <InfoRow label="Entidades cruzadas">
                                    <Typography variant="paragraph" style={{ color: PLD_COLORS.neutral }}>
                                        {entitiesText}
                                    </Typography>
                                </InfoRow>
                                <InfoRow label="Fuentes consultadas">
                                    <Typography variant="paragraph" style={{ color: PLD_COLORS.neutral }}>
                                        {sourcesText}
                                    </Typography>
                                </InfoRow>

                                {showManualRow && (
                                    <InfoRow label="Cruce manual (manual check)">
                                        <ManualCheckValue summary={manualSummary} pldStatus={pldStatus} manualApplicable={externalHasMatch} />
                                    </InfoRow>
                                )}
                            </Box>

                            {/* Alerta dinámica según el estatus */}
                            {currentConfig.alertConfig && (
                                <Alert
                                    severity={currentConfig.alertConfig.severity}
                                    icon={
                                        currentConfig.alertConfig.severity === "error" ? (
                                            <ErrorOutline fontSize="inherit" />
                                        ) : currentConfig.alertConfig.severity === "success" ? (
                                            <CheckCircleOutline fontSize="inherit" />
                                        ) : (
                                            <InfoOutlined fontSize="inherit" />
                                        )
                                    }
                                    sx={{
                                        backgroundColor: currentConfig.alertConfig.bgColor,
                                        border: `1px solid ${currentConfig.alertConfig.borderColor}`,
                                        color: currentConfig.alertConfig.textColor,
                                        borderRadius: 2,
                                    }}
                                >
                                    <AlertTitle sx={{ fontWeight: 700, fontSize: "0.95rem" }}>{currentConfig.alertConfig.title}</AlertTitle>
                                    {alertMessage}
                                </Alert>
                            )}
                        </>
                    )}
                </FlexGrid>
            </Card>

            {/* Modales Vinculados */}
            <ModalBlackListDialog
                open={modalDetailProps.open}
                onClose={() => {
                    modalDetailProps.onClose();
                    setActiveModalData(null);
                }}
                title={activeModalData?.title || "Cruces realizados"}
                blackList={activeModalData?.list || allValidations}
                showManualCheck={showManualCheckColumn}
                onManualCheck={(row) => {
                    modalDetailProps.onClose();
                    setActiveModalData(null);
                    setMatchDetails(row);
                    modalManualCheckProps.openDialog();
                }}
            />

            <ModalManualCheck matchDetails={matchDetails} open={modalManualCheckProps.open} onClose={modalManualCheckProps.onClose} onSaveResult={onSaveManualCheck} />
        </FlexGrid>
    );

    if (onClose !== undefined) {
        return (
            <Dialog open={open} onClose={onClose} style={{ width: 850, maxWidth: "95vw", maxHeight: 600 }}>
                <DialogTitle>
                    <Typography variant="h2" style={{ fontSize: "1.25rem", fontWeight: 700 }}>
                        Cruce en listas negras (PLD)
                    </Typography>
                </DialogTitle>
                <DialogContent>{renderContent()}</DialogContent>
                <DialogActions>
                    <FlexGrid container fullWidth justify="flex-end" alignItems="center">
                        <Button buttonType="secondary" onClick={onClose} size="normal">
                            Cerrar
                        </Button>
                    </FlexGrid>
                </DialogActions>
            </Dialog>
        );
    }

    return renderContent();
};
